import React, { memo, useState, useEffect, FC } from 'react';
import { Modal, Input, Button, Radio, Space } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import * as styles from './WaitlistModal.module.css';
import CommandDialog from '../CommandDialog/CommandDialog';
import { useJoinWaitlist, ApiError } from 'src/api/mutations/useJoinWaitlist.ts';
import { AxiosError } from 'axios';

interface WaitlistModalProps {
  visible: boolean;
  onClose: () => void;
  initialEmail?: string | null;
}

const WaitlistModal: FC<WaitlistModalProps> = ({ visible, onClose, initialEmail }) => {
  const [email, setEmail] = useState<string>('');
  const [userType, setUserType] = useState<'mover' | 'trainer' | undefined>(undefined);

  const [success, setSuccess] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    type?: string;
    general?: string;
  }>({});

  const { mutate: joinWaitlist, isPending } = useJoinWaitlist();

  useEffect(() => {
    if (visible) {
      setEmail(initialEmail || '');
      setUserType(undefined);
      setSuccess(false);
      setFieldErrors({});
    }
  }, [visible, initialEmail]);

  const handleSubmit = () => {
    if (!email || !userType) return;

    setFieldErrors({});

    joinWaitlist(
      { email, type: userType },
      {
        onSuccess: () => {
          setSuccess(true);
        },
        onError: (err) => {
          const axiosErr = err as AxiosError<ApiError>;
          const apiError = axiosErr.response?.data?.error;

          if (!apiError) {
            setFieldErrors({ general: 'Something went wrong. Please try again.' });
            return;
          }

          if (apiError.field === 'email') {
            setFieldErrors({ email: apiError.message });
          } else if (apiError.field === 'type') {
            setFieldErrors({ type: apiError.message });
          } else {
            setFieldErrors({ general: apiError.message });
          }
        },
      },
    );
  };

  const radioOptions = [
    { value: 'mover', label: "I'm a mover", description: 'I want to train with miniPT' },
    { value: 'trainer', label: "I'm a trainer", description: 'I want to coach with miniPT' },
  ];

  const RadioCard = ({ value, label, description }: (typeof radioOptions)[0]) => (
    <div
      className={`${styles.radioCard} ${userType === value ? styles.selected : ''}`}
      onClick={() => setUserType(value as 'mover' | 'trainer')}
    >
      <Radio className={styles.radioCustom} checked={userType === value} value={value}>
        <div className={styles.radioContent}>
          <div className={styles.radioLabel}>{label}</div>
          <div className={styles.radioDescription}>{description}</div>
        </div>
      </Radio>
    </div>
  );

  if (success) {
    return (
      <CommandDialog
        visible={visible}
        onClose={onClose}
        type="success"
        title="You're on the list!"
        description={
          <span>
            We&apos;ve added <strong>{email}</strong> to our waitlist.
          </span>
        }
        confirmText="Done"
      />
    );
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined style={{ fontSize: '20px' }} />}
      className={styles.waitlistModalContainer}
      styles={{ body: { padding: '0px', boxShadow: 'none' } }}
      width={'26.563rem'}
      centered
      rootClassName="edit-waitlist"
    >
      <div className={styles.waitlistModalContent}>
        <div>
          <div className={styles.modalTitle}>{'Join the waitlist'}</div>
          <p className={styles.modalSubtitle}>Be the first to know when miniPT is ready for you.</p>
        </div>

        <div>
          <label className={styles.inputLabel} htmlFor="email-input">
            Email
          </label>
          <Input
            id="email-input"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            status={fieldErrors.email ? 'error' : undefined}
          />
          {fieldErrors.email && (
            <div style={{ color: 'red', marginTop: 4 }}>{fieldErrors.email}</div>
          )}
        </div>

        <Space orientation="horizontal" size={16} className={styles.radioOptionsSpace}>
          {radioOptions.map((option) => (
            <RadioCard key={option.value} {...option} />
          ))}
        </Space>

        {fieldErrors.type && (
          <div style={{ color: 'red', textAlign: 'center' }}>{fieldErrors.type}</div>
        )}

        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={!email || !userType || isPending}
          loading={isPending}
          className={styles.joinButtonCustom}
        >
          {'Join the waitlist'}
        </Button>

        {fieldErrors.general && (
          <div style={{ color: 'red', marginTop: 10, textAlign: 'center' }}>
            {fieldErrors.general}
          </div>
        )}
      </div>
    </Modal>
  );
};

WaitlistModal.displayName = 'WaitlistModal';
export default memo(WaitlistModal);
